import type { CardColumn } from '@/components/CardTable/type';
import {
  ModernPaginationProps,
  Table,
  TableProps,
  TRecordType,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { CSSProperties, memo, ReactNode, useMemo, useRef } from 'react';
import styles from './styles.module.scss';
import { useVirtualTableRows } from './useVirtualTableRows';
import { VirtualTableRow } from './VirtualTableRow';

const cx = classnames.bind(styles);

export type InternalTableProps<RecordType extends TRecordType> = Omit<
  TableProps<RecordType>,
  'pagination' | 'emptyType'
>;

export type Props<RecordType extends TRecordType> = {
  table: InternalTableProps<RecordType>;
  pagination: ModernPaginationProps;
  /** Action placed below the table (e.g. 「残りをすべて表示」). */
  footer?: ReactNode;
  testId: string;
};

/** Every row is this fixed height — see `VirtualTableRow` for why it is
 * deliberately never measured. */
const ROW_HEIGHT = 48;
/** Extra rows kept mounted above/below the viewport to hide scroll seams. */
const ROW_OVERSCAN = 12;

type RecordEntry<RecordType extends TRecordType> = {
  record: RecordType;
  index: number;
};

/**
 * Virtualized table, sharable across any drawer detail table that reuses the
 * design-system `Table` (antd under the hood) so header rendering, column
 * widths/colgroup, and bordered styling all come from it — only the visible
 * window of rows is fed in as `data`, and the tbody gets `before`/`after`
 * spacer `<tr>`s so the scrollbar still reflects the full list (see
 * https://github.com/TanStack/virtual/issues/585#issuecomment-1716173260).
 * It windows against the drawer's scroll container — like the card layout —
 * so it keeps a single scrollbar and grows with the drawer.
 *
 * The body rows themselves are NOT rendered by antd: `columns` handed to
 * `Table` have their `render` swapped for a no-op (cheap for `Table` to
 * compute on every scroll step), and `components.body.row` renders a real
 * `VirtualTableRow` instead, built from the *real* columns and `memo`ized on
 * row key alone. That is what makes scrolling cheap — an already-visible row
 * is never asked to recompute its cells (every `TooltipCell`) just because
 * the window shifted, the same reason the card layout scrolls smoothly.
 *
 * The header is pinned to the top of the drawer via `sticky` (antd renders
 * it as an element separate from the scrolling body specifically so this
 * works — see the `sticky` memo below and the scss module).
 */
const _VirtualTable = <RecordType extends TRecordType>({
  table,
  pagination,
  footer,
  testId,
}: Props<RecordType>) => {
  const { data, columns, ...restTable } = table;
  // `data`/`columns` are typed against antd's own `Table` shape
  // (`dataSource`/`TableColumn`), which doesn't carry the Card-specific
  // fields (`unit`, `caption`, `help`, ...) `CardColumn` adds; both are
  // still `RecordType`-shaped underneath since `InternalTableProps` is
  // sourced from the same columns `useInvoiceTransactionColumns` builds.
  const records = useMemo(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    () => (data ?? []) as RecordType[],
    [data]
  );
  const cols = useMemo(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    () => (columns ?? []) as CardColumn<RecordType>[],
    [columns]
  );

  // Only pin the first column when the caller actually opted into it (see
  // the scss module) — otherwise every table sharing this component would
  // pick up a sticky/bordered first column regardless of its own columns.
  const hasStickyFirstColumn = cols[0]?.fixed === 'left';

  const scroll = useMemo(() => ({ x: 'max-content' }), []);

  // `Table` only needs to know title/width/align/key for header + colgroup
  // sizing; `render` is swapped for a no-op so its own per-cell computation
  // stays cheap (the real cells are rendered by `VirtualTableRow` below).
  // `fixed` is dropped in favour of a plain CSS sticky column (see the scss
  // module) since the body row it relies on is bypassed here.
  const tableColumns = useMemo(
    () =>
      cols.map((column) => ({
        ...column,
        render: () => null,
        fixed: undefined,
      })),
    [cols]
  );

  const getItemKey = useMemo(
    () => (index: number) => records[index]?.id ?? index,
    [records]
  );

  const { wrapperRef, virtualItems, before, after, getScrollElement } =
    useVirtualTableRows({
      count: records.length,
      rowHeight: ROW_HEIGHT,
      overscan: ROW_OVERSCAN,
      getItemKey,
    });

  // Pins the header to the top of the *drawer* while rows scroll underneath
  // (see antd's `Table` `sticky` prop). This only works because
  // `.rci-table-container` (below) doesn't clip overflow — any ancestor
  // with non-`visible` overflow between the header and the drawer would
  // block the header from sticking to anything past it, per how CSS
  // `position: sticky` resolves its containing block.
  const sticky = useMemo(
    () => ({
      getContainer: () => {
        const el = getScrollElement();
        return el instanceof HTMLElement ? el : document.body;
      },
    }),
    [getScrollElement]
  );

  const visibleData = useMemo(
    () =>
      virtualItems
        .map((item) => records[item.index])
        .filter((row): row is RecordType => row !== undefined),
    [virtualItems, records]
  );

  // Rebuilt only when `records` changes (not on scroll), so the row lookup
  // it backs stays stable while scrolling — the key to `VirtualTableRow`'s
  // memoization actually holding.
  const recordsById = useMemo(() => {
    const map = new Map<string, RecordEntry<RecordType>>();
    records.forEach((record, index) => {
      if (record?.id !== undefined) {
        map.set(String(record.id), { record, index });
      }
    });
    return map;
  }, [records]);

  const columnCount = cols.length;

  // antd uses `components.body.wrapper`/`row` as the tbody/tr element
  // *types*. If their identity changed each scroll, React would remount
  // the whole table body every scroll step, so they are created once and
  // read the per-scroll values (spacer heights, row lookups) from a ref.
  const renderStateRef = useRef({
    before,
    after,
    columnCount,
    recordsById,
    realColumns: cols,
  });
  renderStateRef.current = {
    before,
    after,
    columnCount,
    recordsById,
    realColumns: cols,
  };

  const components = useMemo(
    () => ({
      body: {
        wrapper: ({
          className,
          children,
        }: {
          className?: string;
          children?: ReactNode;
        }) => {
          const {
            before: beforeH,
            after: afterH,
            columnCount: colCount,
          } = renderStateRef.current;
          const beforeStyle: CSSProperties = { height: beforeH, padding: 0 };
          const afterStyle: CSSProperties = { height: afterH, padding: 0 };
          return (
            <tbody className={className}>
              {beforeH > 0 && (
                <tr aria-hidden='true'>
                  <td colSpan={colCount} style={beforeStyle} />
                </tr>
              )}
              {children}
              {afterH > 0 && (
                <tr aria-hidden='true'>
                  <td colSpan={colCount} style={afterStyle} />
                </tr>
              )}
            </tbody>
          );
        },
        row: ({
          className,
          children,
          'data-row-key': rowKey,
        }: {
          className?: string;
          children?: ReactNode;
          'data-row-key'?: string;
        }) => {
          // antd renders its own empty-state row (shown when there is no
          // data) through this same slot, but without a `data-row-key`
          // since it isn't tied to a real record — its actual content
          // (the `emptyText` passed to `Table`) arrives as `children`.
          // Only substitute the virtualized row for real data rows;
          // anything without a row key must render that content as-is,
          // or the empty state would silently disappear.
          if (rowKey === undefined) {
            return <tr className={className}>{children}</tr>;
          }

          const { recordsById: byId, realColumns } = renderStateRef.current;
          const entry = byId.get(String(rowKey));
          return (
            <VirtualTableRow
              className={className}
              rowKey={rowKey}
              record={entry?.record}
              index={entry?.index}
              columns={realColumns}
            />
          );
        },
      },
    }),
    []
  );

  return (
    <div
      ref={wrapperRef}
      className={cx('container', { stickyFirstColumn: hasStickyFirstColumn })}
      data-testid={testId}
    >
      <Table<RecordType>
        {...restTable}
        data={visibleData}
        columns={tableColumns}
        emptyType='object'
        legacy={false}
        testId={testId}
        scroll={scroll}
        pagination={pagination}
        rowKey='id'
        bordered
        hideFooterPagination
        tableLayout='fixed'
        components={components}
        sticky={sticky}
      />
      {footer && <div className={cx('footer')}>{footer}</div>}
    </div>
  );
};
_VirtualTable.displayName = '_VirtualTable';

// The assertion restores the generic parameter that memo erases (see
// `RecordCard` for the same pattern).
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const VirtualTable = memo(_VirtualTable) as typeof _VirtualTable;
VirtualTable.displayName = 'VirtualTable';
